import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database Types
export interface UserProfile {
  id: string
  email: string
  full_name: string
  avatar_url?: string
  plan_type: 'free' | 'pro' | 'premium'
  ai_chat_count: number
  ai_chat_last_reset: string
  created_at: string
  updated_at: string
}

export interface DreamTeam {
  id: string
  user_id: string
  name: string
  formation_id: string
  formation_name: string
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface TeamPlayer {
  id: string
  team_id: string
  player_id: string
  player_name: string
  position: string
  jersey_number?: number
  image_url?: string
  api_data: any
  created_at: string
}

export interface TeamSubstitute {
  id: string
  team_id: string
  player_id: string
  player_name: string
  position: string
  jersey_number?: number
  image_url?: string
  api_data: any
  created_at: string
}

export interface UserActivity {
  id: string
  user_id: string
  activity_type: string
  activity_data: any
  created_at: string
}

export interface AIChatHistory {
  id: string
  user_id: string
  message_content: string
  response_content?: string
  tokens_used?: number
  created_at: string
}

// Extended team interface with populated relations
export interface DreamTeamWithPlayers {
  id: string
  user_id: string
  name: string
  formation_id: string
  formation_name: string
  is_default: boolean
  created_at: string
  updated_at: string
  team_players: TeamPlayer[]
  team_substitutes: TeamSubstitute[]
}

// Utility type for database operations
export type Database = {
  public: {
    Tables: {
      user_profiles: {
        Row: UserProfile
        Insert: Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>
        Update: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>
      }
      dream_teams: {
        Row: DreamTeam
        Insert: Omit<DreamTeam, 'id' | 'created_at' | 'updated_at'>
        Update: Partial<Omit<DreamTeam, 'id' | 'created_at' | 'updated_at'>>
      }
      team_players: {
        Row: TeamPlayer
        Insert: Omit<TeamPlayer, 'id' | 'created_at'>
        Update: Partial<Omit<TeamPlayer, 'id' | 'created_at'>>
      }
      team_substitutes: {
        Row: TeamSubstitute
        Insert: Omit<TeamSubstitute, 'id' | 'created_at'>
        Update: Partial<Omit<TeamSubstitute, 'id' | 'created_at'>>
      }
      user_activities: {
        Row: UserActivity
        Insert: Omit<UserActivity, 'id' | 'created_at'>
        Update: Partial<Omit<UserActivity, 'id' | 'created_at'>>
      }
      ai_chat_history: {
        Row: AIChatHistory
        Insert: Omit<AIChatHistory, 'id' | 'created_at'>
        Update: Partial<Omit<AIChatHistory, 'id' | 'created_at'>>
      }
    }
  }
}

// Utility function to check if Supabase is configured
export const isSupabaseConfigured = (): boolean => {
  return !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY)
} 